/**
 * Courier Delivery API Client
 * Epic 71: Futaros Kezbesites
 */

import { ApiError, api } from './client';

// ============================================
// TYPES
// ============================================

export type CourierDeliveryStatus =
  | 'REQUESTED'
  | 'PAYMENT_PENDING'
  | 'PAID'
  | 'DISPATCHED'
  | 'DELIVERED'
  | 'CANCELLED';

export interface CourierDelivery {
  id: string;
  worksheetId: string;
  address: string;
  scheduledDate?: string;
  courierProvider?: string;
  trackingNumber?: string;
  status: CourierDeliveryStatus;
  courierCost?: number;
  handoffAt?: string;
  deliveredAt?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface RequestDeliveryInput {
  worksheetId: string;
  address: string;
  scheduledDate?: string;
  courierProvider?: string;
  courierCost?: number;
}

export interface CourierHandoffInput {
  courierProvider: string;
  trackingNumber?: string;
}

export interface PaymentLink {
  id: string;
  deliveryId: string;
  url: string;
  amount: number;
  status: string;
  expiresAt: string;
  createdAt: string;
}

export interface CreatePaymentLinkInput {
  amount: number;
  description?: string;
}

// ============================================
// API CALLS
// ============================================

export async function getCourierDeliveries(worksheetId?: string, status?: string) {
  const params = new URLSearchParams();
  if (worksheetId) params.set('worksheetId', worksheetId);
  if (status) params.set('status', status);
  const query = params.toString() ? `?${params.toString()}` : '';
  return api.get<{ data: CourierDelivery[] }>(`/courier-deliveries${query}`);
}

export async function getCourierDeliveryById(id: string) {
  return api.get<{ data: CourierDelivery }>(`/courier-deliveries/${id}`);
}

export async function requestCourierDelivery(data: RequestDeliveryInput) {
  return api.post<{ data: CourierDelivery }>('/courier-deliveries', data);
}

export async function updateCourierStatus(id: string, status: string) {
  return api.patch<{ data: CourierDelivery }>(`/courier-deliveries/${id}/status`, { status });
}

export async function handoffToCourier(id: string, data: CourierHandoffInput) {
  return api.post<{ data: CourierDelivery }>(`/courier-deliveries/${id}/handoff`, data);
}

export async function markDelivered(id: string) {
  return api.post<{ data: CourierDelivery }>(`/courier-deliveries/${id}/delivered`, {});
}

export async function createPaymentLink(deliveryId: string, data: CreatePaymentLinkInput) {
  return api.post<{ data: PaymentLink }>(`/courier-deliveries/${deliveryId}/payment-link`, data);
}

// ============================================
// Futáros-művelet hiba-osztályozás (68-kör 1️⃣ · 2/2)
// ============================================

/**
 * A futáros kézbesítés MUTÁCIÓS műveletei (átadás, kézbesítettre állítás,
 * sztornó, fizetési link) hibájának i18n-kulcsra képezése.
 *
 * ⚠️ (A)-alak — 🚦-ratify 2026-09-02: a kanonikus 3-szintű minta (deposits /
 * cash-management) `(1)` STRUKTURÁLT `error.code` ága itt SZÁNDÉKOSAN HIÁNYZIK.
 * Mérve a fán: a BE `service/controllers/courier-delivery.controller.ts` kizárólag
 * `BadRequestException('szöveg')` és `NotFoundException('szöveg')` alakot dob,
 * `{ error: { code } }` boríték nélkül ⇒ az `ApiError.code` mindig `undefined`,
 * a kód-switch minden ága halott lenne. Kitalált kódok = false affordance.
 * A BE strukturált-kód bevezetése kör-69 jelölt (⚙️ lapja); ha megjön, ez a
 * függvény egy `(1)` switch-csel bővül, a hívóhelyek változatlanul maradnak.
 */
export function courierActionErrorToastKey(error: unknown): string {
  if (!(error instanceof ApiError)) return 'courier.actionError.failed';

  // (2) HTTP-status-fallback — ma ez az EGYETLEN diszkrimináló jel
  switch (error.statusCode) {
    case 404:
      return 'courier.actionError.notFound';
    case 409:
      return 'courier.actionError.conflict';
    case 400:
    case 422:
      return 'courier.actionError.validation';
    default:
      return 'courier.actionError.failed';
  }
}
